/**
 * RequestHeader — top card on the Company Registration detail page.
 * Shows request code, status pill, service name, operator, notes.
 * Right-side actions: Print receipt (enabled after close) + Cancel/hard-delete.
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238 refactor.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Hash, Loader2, Printer, Receipt as ReceiptIcon, Trash2, X, Pencil, CalendarDays, Save } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { printReceipt } from '../../utils/printReceipt';
import { API, authHeader } from './helpers';
import { useAuth } from '../../context/AuthContext';

export function RequestHeader({ reg, t, onDelete, onMetaEdited }) {
  // iter-246 (Feb 2026) — Delete/Cancel button is now ADMIN-ONLY per
  // user's rule "الحذف يكون حصراً للـ admin". Staff / accountant / any
  // other role no longer sees the destructive button.
  const { user } = useAuth();
  const isAdminRole = String(user?.role || '').toLowerCase() === 'admin';
  const isCompleted = reg.status === 'completed';
  // iter-234 (Feb 2026) — Print receipt button appears at the top of the
  // form, but only becomes enabled AFTER the registration is fully closed
  // (`status=completed` set server-side when the last stage is ticked).
  // Clicking opens a small language-picker modal (AR/EN) before generating
  // the same-brand receipt PDF used for assigned tasks.
  const [pickLang, setPickLang] = React.useState(false);
  const [pickReportLang, setPickReportLang] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // iter-235 (Feb 2026) — Cancel/hard-delete flow with typed confirmation.
  const [confirmDel, setConfirmDel]   = React.useState(false);
  const [confirmText, setConfirmText] = React.useState('');
  const [deleting, setDeleting]       = React.useState(false);
  // iter-264d (Feb 2026) — Admin-only meta editor for request_code + created_at.
  const [editMeta, setEditMeta]       = React.useState(false);
  const [metaCode, setMetaCode]       = React.useState('');
  const [metaDate, setMetaDate]       = React.useState('');
  const [metaReason, setMetaReason]   = React.useState('');
  const [savingMeta, setSavingMeta]   = React.useState(false);
  const openMetaEditor = () => {
    setMetaCode(reg.request_code || '');
    // Prefill with plain YYYY-MM-DD so the date input works.
    const iso = reg.created_at || '';
    setMetaDate(iso ? String(iso).slice(0, 10) : '');
    setMetaReason('');
    setEditMeta(true);
  };
  const saveMeta = async () => {
    const payload = {};
    const trimmedCode = (metaCode || '').trim().toUpperCase();
    if (trimmedCode && trimmedCode !== (reg.request_code || '').toUpperCase()) {
      payload.request_code = trimmedCode;
    }
    // Only send created_at if the operator changed the date.
    if (metaDate && !String(reg.created_at || '').startsWith(metaDate)) {
      payload.created_at = metaDate;
    }
    if (Object.keys(payload).length === 0) {
      toast.error(t('لا توجد تغييرات', 'No changes'));
      return;
    }
    payload.reason = (metaReason || '').trim();
    setSavingMeta(true);
    try {
      const r = await axios.patch(
        `${API}/api/admin/company-registrations/${reg.id}/edit-meta`,
        payload,
        { headers: authHeader() },
      );
      toast.success(t('تم تحديث بيانات الطلب', 'Request meta updated'));
      setEditMeta(false);
      if (typeof onMetaEdited === 'function') onMetaEdited(r.data?.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingMeta(false);
    }
  };

  const doPrint = async (lang) => {
    setPickLang(false);
    setBusy(true);
    try {
      // Receipt is auto-created server-side when the "closed" stage is added
      // (see admin_company_registrations._ensure_registration_receipt). We
      // fetch it by the shim task_id COMPREG-<reg.id>.
      const taskId = `COMPREG-${reg.id}`;
      let rc = null;
      try {
        const r = await axios.get(`${API}/api/admin/receipts/by-task/${taskId}`, { headers: authHeader() });
        rc = r.data;
      } catch (err) {
        toast.error(t('لم يتم إنشاء الإيصال بعد — أعد فتح الطلب أو أعد إغلاقه',
                      'Receipt not created yet — reopen / close the request again'));
        return;
      }
      // iter-234 — User request: print block shows license + admin ONLY
      // (drop gov_fees breakdown), plus service name & authority.
      const licAmt = Number(reg?.license_fees?.amount || 0);
      const afMode = reg?.admin_fees?.mode || 'admin_fees';
      const adAmt  = afMode === 'admin_fees' ? Number(reg?.admin_fees?.amount || 0) : 0;
      const items = [];
      if (licAmt > 0) items.push({ label_ar: 'رسوم الرخصة', label_en: 'License fees', amount: licAmt });
      if (adAmt > 0)  items.push({ label_ar: 'الرسوم الخاصة بنا', label_en: 'Our Fees', amount: adAmt });
      const authorityAr = reg.authority_name_ar || reg.authority_name_en || '';
      const authorityEn = reg.authority_name_en || reg.authority_name_ar || '';
      const enrichedReceipt = {
        ...rc,
        service_name_ar: rc.service_name_ar || 'تسجيل شركة جديدة',
        service_name_en: rc.service_name_en || 'New Company Registration',
        items,
        authority_name_ar: authorityAr,
        authority_name_en: authorityEn,
        // iter-243 — Company registration reaches the receipt stage only
        // after full collection.
        status: rc.status || 'paid',
      };
      try { printReceipt(enrichedReceipt, { language: lang }); }
      catch { toast.error(t('فشل فتح الطباعة', 'Print failed')); }
    } finally {
      setBusy(false);
    }
  };

  // iter-244 (Feb 2026) — Full request-report print (works at ANY status).
  // Renders the SAME beautiful receipt template with 3 extra sections:
  // company details, fee breakdown, and stages timeline. Client asked for
  // "بيانات الشركة + العميل + كل المراحل مع تواريخها + المبالغ
  // (رسوم/عمولة/إجمالي) + الحالة + الخدمة و رقم الطلب و كل البيانات".
  const [reportBusy, setReportBusy] = React.useState(false);
  const printReport = async (lang) => {
    setReportBusy(true);
    try {
      const isAr    = lang === 'ar';
      const client  = reg.client || {};
      const tl      = reg.trade_license || {};
      const lf      = reg.license_fees || {};
      const af      = reg.admin_fees || {};
      const gov     = reg.gov_fees || {};
      const agent   = reg.agent || {};

      const licAmt  = Number(lf.amount || 0);
      const afMode  = af.mode || 'admin_fees';
      const adAmt   = afMode === 'admin_fees' ? Number(af.amount || 0) : 0;
      const commAmt = afMode === 'commission'  ? Number(af.amount || 0) : 0;
      const govEntries = Array.isArray(gov.entries) ? gov.entries : [];
      const govTotal = govEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
      const agentCommission = Number(agent.commission_amount || 0);

      const fees = [];
      if (licAmt   > 0) fees.push({ label_ar: 'رسوم الرخصة',           label_en: 'License fees',        amount: licAmt });
      if (adAmt    > 0) fees.push({ label_ar: 'الرسوم الخاصة بنا',       label_en: 'Our Fees',          amount: adAmt });
      if (commAmt  > 0) fees.push({ label_ar: 'كومشن التسجيل',         label_en: 'Registration comm.',  amount: commAmt });
      if (govTotal > 0) fees.push({ label_ar: 'رسوم حكومية إضافية',    label_en: 'Extra government fees', amount: govTotal });
      if (agentCommission > 0) fees.push({ label_ar: 'عمولة الوكيل',    label_en: 'Agent commission',    amount: agentCommission });
      const total = fees.reduce((s, x) => s + Number(x.amount || 0), 0);

      // Build the stages timeline directly from reg.stages (persisted list).
      const stageLabels = {
        pending_review:       ['قيد المراجعة',             'Pending review'],
        approved:             ['تمت الموافقة',             'Approved'],
        in_progress:          ['قيد التنفيذ',              'In progress'],
        docs_prepared:        ['تجهيز الوثائق',            'Documents prepared'],
        docs_submitted:       ['تقديم الوثائق للجهة',      'Submitted to authority'],
        license_paid:         ['دفع رسوم الرخصة',          'License fees paid'],
        license_issued:       ['إصدار الرخصة',             'License issued'],
        fees_collection:      ['تحصيل الرسوم',             'Fee collection'],
        company_file_opened:  ['فتح ملف الشركة',           'Company file opened'],
        commission_collected: ['تحصيل عمولة الوكيل',       'Agent commission collected'],
        commission_sent:      ['تم إرسال العمولة',         'Commission sent'],
        closed:               ['تم الانتهاء وإغلاق الطلب', 'Closed & finalised'],
      };
      const stagesTimeline = (reg.stages || []).map((s) => ({
        stage:    s.stage,
        label_ar: stageLabels[s.stage]?.[0] || s.stage,
        label_en: stageLabels[s.stage]?.[1] || s.stage,
        date:     s.date,
        actor:    s.actor || s.assignee_name || '',
        note:     s.note || '',
      }));

      const isClosed = reg.status === 'completed';
      const status   = isClosed ? 'paid' : 'unpaid';

      const rc = {
        // Header
        receipt_no:    reg.request_code || reg.code || '—',
        request_no:    reg.request_code || reg.code || '',
        currency:      'AED',
        created_at:    reg.created_at,
        paid_at:       isClosed ? (reg.updated_at || reg.created_at) : null,
        status,
        amount_aed:    total,
        // Payer
        payer_name:    client.name  || '',
        payer_phone:   client.phone || '',
        payer_email:   client.email || '',
        // Service
        service_name_ar:     'تسجيل شركة جديدة',
        service_name_en:     'New Company Registration',
        sub_service_name_ar: tl.company_name_ar || '',
        sub_service_name_en: tl.company_name_en || '',
        payment_method: 'bank_transfer',
        // Extra sections
        company_details: {
          name_ar:        tl.company_name_ar || '',
          name_en:        tl.company_name_en || '',
          license_number: tl.license_number  || '',
          authority:      tl.issuing_authority || reg.authority_name_ar || reg.authority_name_en || '',
          legal_form:     tl.legal_form      || '',
          license_type:   tl.license_type    || '',
          issue_date:     tl.issue_date      || '',
          expiry_date:    tl.expiry_date     || '',
        },
        fee_breakdown:  fees,
        fee_total:      total,
        stages_timeline: stagesTimeline,
      };

      try { printReceipt(rc, { language: lang }); }
      catch { toast.error(t('فشل فتح الطباعة', 'Print failed')); }
    } finally {
      setReportBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#151530] to-[#0f0f24] p-6" data-testid="reg-request-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Hash className="h-4 w-4 text-emerald-400" />
            <span className="text-white text-lg font-mono font-bold" dir="ltr" data-testid="request-code">
              {reg.request_code}
            </span>
            {isAdminRole && (
              <button
                type="button"
                onClick={openMetaEditor}
                className="p-1.5 rounded-md bg-white/5 hover:bg-white/15 text-white/60 hover:text-white border border-white/10"
                title={t('تعديل رقم الطلب والتاريخ (Admin)', 'Edit request code & date (Admin)')}
                data-testid="reg-edit-meta-btn"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            <span
              className={
                'text-xs px-2 py-0.5 rounded-full font-semibold ' +
                (isCompleted
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                  : 'bg-amber-500/15 text-amber-300 border border-amber-500/30')
              }
            >
              {isCompleted ? t('مكتمل', 'Completed') : t('قيد الإجراء', 'In progress')}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs text-[#71717A]">{t('الخدمة', 'Service')}:</span>
            <span className="text-white font-semibold">{t('تسجيل شركة جديدة', 'New Company Registration')}</span>
          </div>
          <div className="text-xs text-[#71717A] mt-1 flex items-center gap-2 flex-wrap">
            <span>{t('منفذ المهمة', 'Executor')}: <span className="text-white">{reg.assignee_name || reg.assignee_email || '—'}</span></span>
            {reg.created_at && (
              <span className="flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                <span className="text-white" dir="ltr">
                  {String(reg.created_at).slice(0, 10)}
                </span>
              </span>
            )}
          </div>
          {reg.notes ? (
            <div className="mt-3 p-3 rounded-xl bg-black/40 border border-white/10 text-sm text-white/90 whitespace-pre-wrap">
              {reg.notes}
            </div>
          ) : null}
        </div>
        {/* iter-234 — Print receipt (top-right). Disabled until closed.
           iter-235 — Cancel/Delete button next to it. */}
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* iter-244 — Full report print — available at ANY status. */}
            <button
              type="button"
              disabled={reportBusy}
              onClick={() => setPickReportLang(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-sky-500/15 border-sky-500/40 text-sky-200 hover:bg-sky-500/25 transition disabled:opacity-50"
              title={t('طباعة تقرير الطلب الكامل', 'Print full request report')}
              data-testid="reg-print-report-btn"
            >
              {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {t('طباعة تقرير الطلب', 'Print Request Report')}
            </button>
            <button
              type="button"
              disabled={!isCompleted || busy}
              onClick={() => setPickLang(true)}
              className={
                'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition ' +
                (isCompleted && !busy
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25'
                  : 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed')
              }
              title={isCompleted
                ? t('طباعة إيصال', 'Print receipt')
                : t('يفعّل بعد إغلاق الطلب من المراحل', 'Enables after closing the request')}
              data-testid="reg-print-receipt-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptIcon className="w-4 h-4" />}
              {t('طباعة الإيصال', 'Print Receipt')}
            </button>
            {/* Cancel / hard-delete — admin-only (iter-246). */}
            {isAdminRole && (
              <button
                type="button"
                disabled={deleting}
                onClick={() => { setConfirmText(''); setConfirmDel(true); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-rose-500/15 border-rose-500/40 text-rose-200 hover:bg-rose-500/25 transition disabled:opacity-50"
                title={t('حذف الطلب نهائياً', 'Delete request permanently')}
                data-testid="reg-cancel-btn"
              >
                <Trash2 className="w-4 h-4" />
                {t('إلغاء الطلب', 'Cancel Request')}
              </button>
            )}
          </div>
          {!isCompleted && (
            <span className="text-[10px] text-white/40">
              {t('يفعّل زر الطباعة بعد إغلاق الطلب', 'Print enables after closing')}
            </span>
          )}
        </div>
      </div>

      {/* iter-234 — Language picker modal (AR / EN). Same pattern used in
         AssignedTaskDetailPage but rendered inline as a proper modal so it
         works on mobile touch (hover-menu was unreliable on the phones). */}
      {pickLang && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
             onClick={() => setPickLang(false)} data-testid="reg-print-lang-modal">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0a0a19] p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <Printer className="w-4 h-4 text-emerald-300" />
                {t('اختر لغة الإيصال', 'Choose receipt language')}
              </h3>
              <button type="button" onClick={() => setPickLang(false)} className="text-white/40 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => doPrint('ar')}
                className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 font-semibold flex flex-col items-center gap-1"
                data-testid="reg-print-lang-ar"
              >
                <Printer className="w-5 h-5" />
                <span>عربي</span>
                <span className="text-[10px] text-emerald-300/70 font-mono">AR</span>
              </button>
              <button
                type="button"
                onClick={() => doPrint('en')}
                className="p-4 rounded-xl border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-200 font-semibold flex flex-col items-center gap-1"
                data-testid="reg-print-lang-en"
              >
                <Printer className="w-5 h-5" />
                <span>English</span>
                <span className="text-[10px] text-sky-300/70 font-mono">EN</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter-244 — Report language picker */}
      {pickReportLang && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
             onClick={() => setPickReportLang(false)} data-testid="reg-print-report-lang-modal">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0a0a19] p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-sky-300" />
                {t('اختر لغة التقرير', 'Choose report language')}
              </h3>
              <button type="button" onClick={() => setPickReportLang(false)} className="text-white/40 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setPickReportLang(false); printReport('ar'); }}
                className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 font-semibold flex flex-col items-center gap-1"
                data-testid="reg-print-report-lang-ar"
              >
                <FileText className="w-5 h-5" />
                <span>عربي</span>
                <span className="text-[10px] text-emerald-300/70 font-mono">AR</span>
              </button>
              <button
                type="button"
                onClick={() => { setPickReportLang(false); printReport('en'); }}
                className="p-4 rounded-xl border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-200 font-semibold flex flex-col items-center gap-1"
                data-testid="reg-print-report-lang-en"
              >
                <FileText className="w-5 h-5" />
                <span>English</span>
                <span className="text-[10px] text-sky-300/70 font-mono">EN</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter-235 — Cancel/hard-delete confirmation modal. Requires typing
         the request_code verbatim to arm the destructive action. */}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
             onClick={() => !deleting && setConfirmDel(false)} data-testid="reg-cancel-modal">
          <div className="w-full max-w-md rounded-2xl border border-rose-500/40 bg-gradient-to-br from-[#1a0f0f] to-[#0a0a0a] p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-rose-300" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base">{t('إلغاء وحذف الطلب', 'Cancel & Delete Request')}</h3>
                <p className="text-xs text-rose-300/80">{t('هذا الإجراء نهائي ولا يمكن التراجع عنه', 'This action is permanent and irreversible')}</p>
              </div>
            </div>
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-xs text-rose-100 mb-3">
              {t(
                `سيتم حذف الطلب "${reg.request_code}" وكل بياناته ومرفقاته ومراحله. للتأكيد، اكتب رقم الطلب بالأسفل:`,
                `Request "${reg.request_code}" with all its data, attachments and stages will be removed. To confirm, type the request code below:`,
              )}
            </div>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={reg.request_code}
              dir="ltr"
              className="bg-[#0a0a0a] border-rose-500/40 text-white rounded-xl mb-4 font-mono"
              data-testid="reg-cancel-confirm-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={deleting} onClick={() => setConfirmDel(false)}
                      className="text-white/60 hover:bg-white/5" data-testid="reg-cancel-abort">
                {t('تراجع', 'Abort')}
              </Button>
              <Button
                disabled={confirmText.trim() !== reg.request_code || deleting}
                onClick={async () => {
                  setDeleting(true);
                  try { await onDelete(); }
                  catch { setDeleting(false); }
                }}
                className="bg-rose-500 hover:bg-rose-600 text-white gap-2 disabled:opacity-40"
                data-testid="reg-cancel-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {t('حذف نهائي', 'Delete forever')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* iter-264d — Meta editor: request_code + created_at (admin-only) */}
      {editMeta && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
             onClick={() => !savingMeta && setEditMeta(false)} data-testid="reg-edit-meta-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a19] p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <Pencil className="w-4 h-4 text-emerald-300" />
                {t('تعديل بيانات الطلب', 'Edit Request Meta')}
              </h3>
              <button type="button" disabled={savingMeta} onClick={() => setEditMeta(false)} className="text-white/40 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('رقم الطلب', 'Request Code')}</label>
                <Input
                  value={metaCode}
                  onChange={(e) => setMetaCode(e.target.value.toUpperCase())}
                  dir="ltr"
                  className="bg-black/40 border-white/10 text-white font-mono"
                  data-testid="reg-edit-meta-code-input"
                />
                <p className="text-[10px] text-white/40 mt-1" dir="ltr">
                  {t('مثال: COCGB-2025-0000047', 'Example: COCGB-2025-0000047')}
                </p>
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('تاريخ الطلب', 'Request Date')}</label>
                <Input
                  type="date"
                  value={metaDate}
                  onChange={(e) => setMetaDate(e.target.value)}
                  dir="ltr"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="reg-edit-meta-date-input"
                />
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1">{t('سبب التعديل (اختياري)', 'Reason (optional)')}</label>
                <Input
                  value={metaReason}
                  onChange={(e) => setMetaReason(e.target.value)}
                  placeholder={t('يُسجل في سجل التدقيق', 'Recorded in the audit trail')}
                  className="bg-black/40 border-white/10 text-white text-sm"
                  data-testid="reg-edit-meta-reason-input"
                />
              </div>
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25 text-[11px] text-amber-100">
                {t(
                  'كل تعديل يُحفظ في سجل مراحل الطلب مع اسم المستخدم والتاريخ.',
                  'Every change is stored in the request stages audit trail with the operator and timestamp.',
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" disabled={savingMeta} onClick={() => setEditMeta(false)}
                      className="text-white/60 hover:bg-white/5" data-testid="reg-edit-meta-cancel">
                {t('إلغاء', 'Cancel')}
              </Button>
              <Button
                disabled={savingMeta}
                onClick={saveMeta}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="reg-edit-meta-save"
              >
                {savingMeta ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
